import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  DollarSign, 
  ShoppingCart, 
  Users, 
  Package, 
  TrendingUp, 
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { GoalsSection } from '../components/GoalsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_sales_today: 0,
    total_sales_month: 0,
    total_orders: 0,
    pending_orders: 0,
    low_stock_products: 0,
    total_clients: 0
  });
  const [salesReport, setSalesReport] = useState({ daily_totals: [], by_payment_method: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reportRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`),
        axios.get(`${API_URL}/api/reports/sales`)
      ]);
      setStats(statsRes.data);
      setSalesReport(reportRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const statCards = [
    {
      title: 'Vendas Hoje',
      value: formatCurrency(stats.total_sales_today),
      icon: DollarSign,
      color: 'blue',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Vendas do Mês',
      value: formatCurrency(stats.total_sales_month),
      icon: TrendingUp,
      color: 'emerald',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Total de Clientes',
      value: stats.total_clients,
      icon: Users,
      color: 'violet',
      change: '+5',
      changeType: 'positive'
    },
    {
      title: 'Pedidos Pendentes',
      value: stats.pending_orders,
      icon: ShoppingCart,
      color: 'amber',
      change: stats.pending_orders > 5 ? 'Atenção' : 'Normal',
      changeType: stats.pending_orders > 5 ? 'negative' : 'positive'
    },
    {
      title: 'Total de Pedidos',
      value: stats.total_orders,
      icon: Package,
      color: 'cyan',
      change: '+3',
      changeType: 'positive'
    },
    {
      title: 'Estoque Baixo',
      value: stats.low_stock_products,
      icon: AlertTriangle,
      color: 'red',
      change: stats.low_stock_products > 0 ? 'Repor!' : 'OK',
      changeType: stats.low_stock_products > 0 ? 'negative' : 'positive'
    }
  ];

  const colorMap = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    violet: 'bg-violet-50 text-violet-600 border-violet-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200',
    red: 'bg-red-50 text-red-600 border-red-200'
  };

  const chartData = salesReport.daily_totals.slice(-7).map(item => ({
    name: new Date(item.date).toLocaleDateString('pt-BR', { weekday: 'short' }),
    value: item.total
  }));

  const paymentData = Object.entries(salesReport.by_payment_method || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value: value
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Visão geral do seu negócio</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={index} 
            className="border border-slate-200 hover:border-blue-300 transition-colors"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${colorMap[stat.color]}`}>
                  <stat.icon size={18} />
                </div>
                <span className={`text-xs font-medium flex items-center gap-1 ${
                  stat.changeType === 'positive' ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {stat.changeType === 'positive' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {stat.change}
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-500 mt-1">{stat.title}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Metas de Vendas */}
      <GoalsSection />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Vendas dos Últimos 7 Dias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} tickFormatter={(value) => `R$${value}`} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Vendas']}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#2563EB" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods Chart */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Vendas por Forma de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={paymentData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" fontSize={12} tickFormatter={(value) => `R$${value}`} />
                  <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={12} width={80} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Total']}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#2563EB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <a href="/vendas" className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <ShoppingCart size={20} />
              </div>
              <span className="font-medium text-slate-700">Nova Venda</span>
            </a>
            <a href="/clientes" className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                <Users size={20} />
              </div>
              <span className="font-medium text-slate-700">Novo Cliente</span>
            </a>
            <a href="/produtos" className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
              <div className="p-2 rounded-lg bg-violet-100 text-violet-600">
                <Package size={20} />
              </div>
              <span className="font-medium text-slate-700">Novo Produto</span>
            </a>
            <a href="/pedidos" className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                <TrendingUp size={20} />
              </div>
              <span className="font-medium text-slate-700">Novo Pedido</span>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
